import React from 'react';

interface SegmentExamProps {
  anterior: {
    conjunctiva: string;
    cornea: string;
    anterior_chamber: string;
    iris: string;
    pupil: string;
    lens: string;
  };
  posterior: {
    vitreous: string;
    optic_disc: string;
    macula: string;
    retina: string;
    vessels: string;
  };
  onAnteriorChange: (field: string, value: any) => void;
  onPosteriorChange: (field: string, value: any) => void;
}

const SELECT_OPTIONS: Record<string, Array<{ value: string; label: string }>> = {
  conjunctiva: [
    { value: '', label: 'Seleccionar...' },
    { value: 'normal', label: 'Normal' },
    { value: 'hiperémica', label: 'Hiperémica' },
    { value: 'quemosis', label: 'Quemosis' },
    { value: 'folículos', label: 'Folículos' },
    { value: 'papilas', label: 'Papilas' },
    { value: 'hemorragia subconjuntival', label: 'Hemorragia Subconjuntival' },
  ],
  cornea: [
    { value: '', label: 'Seleccionar...' },
    { value: 'transparente', label: 'Transparente' },
    { value: 'edema', label: 'Edema' },
    { value: 'opacidad', label: 'Opacidad' },
    { value: 'leucoma', label: 'Leucoma' },
    { value: 'queratitis', label: 'Queratitis' },
    { value: 'úlcera', label: 'Úlcera' },
  ],
  anterior_chamber: [
    { value: '', label: 'Seleccionar...' },
    { value: 'normal', label: 'Normal' },
    { value: 'profunda', label: 'Profunda' },
    { value: 'superficial', label: 'Superficial' },
    { value: 'tyndall positivo', label: 'Tyndall Positivo' },
  ],
  iris: [
    { value: '', label: 'Seleccionar...' },
    { value: 'normal', label: 'Normal' },
    { value: 'atrófico', label: 'Atrófico' },
    { value: 'rubeosis', label: 'Rubeosis' },
    { value: 'coloboma', label: 'Coloboma' },
    { value: 'sinequias', label: 'Sinequias' },
  ],
  pupil: [
    { value: '', label: 'Seleccionar...' },
    { value: 'reactiva', label: 'Reactiva' },
    { value: 'isocórica', label: 'Isocórica' },
    { value: 'anisocórica', label: 'Anisocórica' },
    { value: 'midriática', label: 'Midriática' },
    { value: 'miótica', label: 'Miótica' },
  ],
  lens: [
    { value: '', label: 'Seleccionar...' },
    { value: 'transparente', label: 'Transparente' },
    { value: 'catarata incipiente', label: 'Catarata Incipiente' },
    { value: 'catarata madura', label: 'Catarata Madura' },
    { value: 'catarata hipermadura', label: 'Catarata Hipermadura' },
    { value: 'pseudofaquia', label: 'Pseudofaquia' },
    { value: 'afaquia', label: 'Afaquia' },
  ],
  vitreous: [
    { value: '', label: 'Seleccionar...' },
    { value: 'transparente', label: 'Transparente' },
    { value: 'hemovítreo', label: 'Hemovítreo' },
    { value: 'desprendimiento posterior', label: 'Desprendimiento Posterior' },
    { value: 'floaters', label: 'Floaters' },
  ],
  optic_disc: [
    { value: '', label: 'Seleccionar...' },
    { value: 'normal', label: 'Normal' },
    { value: 'pálido', label: 'Pálido' },
    { value: 'edematoso', label: 'Edematoso' },
    { value: 'excavado', label: 'Excavado' },
    { value: 'drusas', label: 'Drusas' },
  ],
  macula: [
    { value: '', label: 'Seleccionar...' },
    { value: 'normal', label: 'Normal' },
    { value: 'edema', label: 'Edema' },
    { value: 'hemorragia', label: 'Hemorragia' },
    { value: 'drusas', label: 'Drusas' },
    { value: 'agujero macular', label: 'Agujero Macular' },
    { value: 'degeneración', label: 'Degeneración' },
  ],
  retina: [
    { value: '', label: 'Seleccionar...' },
    { value: 'normal', label: 'Normal' },
    { value: 'hemorragias', label: 'Hemorragias' },
    { value: 'exudados', label: 'Exudados' },
    { value: 'desprendimiento', label: 'Desprendimiento' },
    { value: 'retinopatía', label: 'Retinopatía' },
  ],
  vessels: [
    { value: '', label: 'Seleccionar...' },
    { value: 'normales', label: 'Normales' },
    { value: 'tortuosos', label: 'Tortuosos' },
    { value: 'estrechamiento', label: 'Estrechamiento' },
    { value: 'neovasos', label: 'Neovasos' },
    { value: 'oclusión', label: 'Oclusión' },
  ],
};

const SegmentExam: React.FC<SegmentExamProps> = ({ anterior, posterior, onAnteriorChange, onPosteriorChange }) => {
  return (
    <>
      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">
            <i className="fa fa-search me-1"></i>
            Segmento Anterior
          </h5>
          <div className="row mt-3">
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Conjuntiva</label>
                <select className="form-select" value={anterior.conjunctiva} onChange={e => onAnteriorChange('conjunctiva', e.target.value)}>
                  {SELECT_OPTIONS.conjunctiva.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Córnea</label>
                <select className="form-select" value={anterior.cornea} onChange={e => onAnteriorChange('cornea', e.target.value)}>
                  {SELECT_OPTIONS.cornea.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Cámara Anterior</label>
                <select className="form-select" value={anterior.anterior_chamber} onChange={e => onAnteriorChange('anterior_chamber', e.target.value)}>
                  {SELECT_OPTIONS.anterior_chamber.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Iris</label>
                <select className="form-select" value={anterior.iris} onChange={e => onAnteriorChange('iris', e.target.value)}>
                  {SELECT_OPTIONS.iris.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Pupila</label>
                <select className="form-select" value={anterior.pupil} onChange={e => onAnteriorChange('pupil', e.target.value)}>
                  {SELECT_OPTIONS.pupil.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Cristalino</label>
                <select className="form-select" value={anterior.lens} onChange={e => onAnteriorChange('lens', e.target.value)}>
                  {SELECT_OPTIONS.lens.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">
            <i className="fa fa-eye-slash me-1"></i>
            Segmento Posterior (Fondo de Ojo)
          </h5>
          <div className="row mt-3">
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Vítreo</label>
                <select className="form-select" value={posterior.vitreous} onChange={e => onPosteriorChange('vitreous', e.target.value)}>
                  {SELECT_OPTIONS.vitreous.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Disco Óptico</label>
                <select className="form-select" value={posterior.optic_disc} onChange={e => onPosteriorChange('optic_disc', e.target.value)}>
                  {SELECT_OPTIONS.optic_disc.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Mácula</label>
                <select className="form-select" value={posterior.macula} onChange={e => onPosteriorChange('macula', e.target.value)}>
                  {SELECT_OPTIONS.macula.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Retina</label>
                <select className="form-select" value={posterior.retina} onChange={e => onPosteriorChange('retina', e.target.value)}>
                  {SELECT_OPTIONS.retina.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Vasos Retinianos</label>
                <select className="form-select" value={posterior.vessels} onChange={e => onPosteriorChange('vessels', e.target.value)}>
                  {SELECT_OPTIONS.vessels.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SegmentExam;
